/* eslint-disable react-hooks/exhaustive-deps */
/*
 * ItemsCatalogTab — public browser for every item in the Palworld data
 * dump (1,967 entries with icons + 2 placeholder-only). Sister tab to
 * the Tech Tree and Base Structures viewers on /tech-tree. Backed by
 * `/api/items-catalog` (categories) and `/api/items-catalog?q=&category=`.
 *
 * Performance note: with 1,969 rows we keep the wire response under
 * 500 rows by default (the backend's `limit` query). The category +
 * search filter usually shrinks this far below the cap, but the
 * "truncated" flag from the backend warns the user when they need to
 * narrow further.
 */
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Loader } from "@/components/Atoms";
import { Search, Package, X, Filter, Link2, Hammer, Wand2 } from "lucide-react";
import { resolveImg } from "@/lib/resolveImg";

function ItemCard({ it, onClick }) {
  const img = resolveImg(it.image);
  return (
    <button
      type="button"
      onClick={onClick}
      className="glass-card p-3 text-left hover:ring-1 hover:ring-[var(--teal)]/40 transition-transform hover:-translate-y-0.5 cursor-pointer flex items-center gap-3"
      data-testid={`item-card-${it.id}`}
      title={it.id}
    >
      <div className="w-12 h-12 rounded bg-black/40 flex items-center justify-center shrink-0 ring-1 ring-white/10">
        {img ? (
          <img src={img} alt={it.name} className="w-10 h-10 object-contain" loading="lazy" draggable={false} />
        ) : (
          <Package size={20} className="text-[var(--muted)]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[var(--bone)] truncate">{it.name}</div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] truncate">{it.category}</div>
      </div>
    </button>
  );
}

function ItemModal({ it, onClose, onUsedInClick }) {
  const img = resolveImg(it.image);
  // Per-item detail is fetched on open — list endpoint stays light
  // (description / recipes ship in `gamedata/item_details.json` and
  // would be ~600 KB extra payload to ship inline).
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => {
    if (!it?.id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/items-catalog/${encodeURIComponent(it.id)}`)
      .then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch(() => { if (!cancelled) setDetail({ description: it.description || "", materials: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [it?.id]);

  const description = detail?.description || it.description || "";
  const materials = detail?.materials || [];
  return (
    <div
      className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4 backdrop-blur-sm fade-up"
      onClick={onClose}
      data-testid="item-modal"
    >
      <div className="glass-card relative max-w-md w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--bone)] p-1 rounded-full hover:bg-white/5 z-10" aria-label="Close" data-testid="item-modal-close">
          <X size={18} />
        </button>
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-20 h-20 rounded-lg ring-1 ring-[var(--teal-soft)] bg-[rgba(0,229,255,0.06)] flex items-center justify-center shrink-0">
              {img ? <img src={img} alt={it.name} className="w-16 h-16 object-contain" /> : <Package size={36} className="text-[var(--teal)]" />}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-semibold tracking-tight" data-testid="item-modal-name">{it.name}</h2>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--teal)] border border-[var(--teal-soft)] inline-block px-2 py-0.5 rounded-full mt-1.5">
                {it.category}
              </div>
              <div className="text-[10px] text-[var(--muted)] mt-1.5 font-mono break-all">{it.id}</div>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-[var(--muted)] italic mb-4" data-testid="item-modal-loading">Loading details…</p>
          ) : description ? (
            <p className="text-sm leading-relaxed text-white/85 whitespace-pre-wrap mb-4" data-testid="item-modal-desc">{description}</p>
          ) : (
            <p className="text-sm text-[var(--muted)] italic mb-4">No description on file for this item.</p>
          )}

          {materials.length > 0 && (
            <div className="mb-4" data-testid="item-modal-recipe">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2 flex items-center gap-2">
                <Hammer size={11} /> Crafting Materials
              </div>
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {materials.map((m, i) => {
                  const mimg = resolveImg(m.image);
                  return (
                    <li
                      key={i}
                      onClick={() => onUsedInClick?.(m.item)}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded bg-black/20 border border-white/5 hover:border-[var(--teal-soft)] hover:bg-[rgba(0,229,255,0.04)] cursor-pointer transition-colors"
                      title={`See every tech that needs ${m.item}`}
                      data-testid={`item-modal-mat-${i}`}
                    >
                      {mimg ? (
                        <img src={mimg} alt={m.item} className="w-8 h-8 rounded object-contain bg-black/30 ring-1 ring-white/10 shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-black/30 ring-1 ring-white/10 shrink-0" />
                      )}
                      <span className="text-sm text-white/85 flex-1 truncate">{m.item}</span>
                      <span className="font-mono text-[var(--teal)] text-sm">×{m.qty}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[10px] text-[var(--muted)] mt-1.5 text-right">
                Click any material to see every tech that uses it.
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            {materials.length > 0 && (
              <RouterLink
                to={`/planner?target=${encodeURIComponent(it.name)}&qty=1`}
                className="flex-1 btn btn-primary text-sm flex items-center justify-center gap-2"
                data-testid="item-modal-plan-craft"
                title={`Open the Crafting Planner with ${it.name} pre-selected`}
              >
                <Wand2 size={14} /> Plan this craft
              </RouterLink>
            )}
            <button
              type="button"
              onClick={() => onUsedInClick?.(it.name)}
              className={`${materials.length > 0 ? "flex-1" : "w-full"} btn btn-secondary text-sm flex items-center justify-center gap-2`}
              data-testid="item-modal-used-in"
              title="See every tech that needs this item as a crafting material"
            >
              <Link2 size={14} /> Where is this used?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CatChip({ active, onClick, label, count, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider border transition-colors flex items-center gap-1.5 ${
        active
          ? "text-[var(--teal)] border-[var(--teal)]/60 bg-[rgba(0,229,255,0.08)]"
          : "text-[var(--muted)] border-white/10 hover:text-white"
      }`}
    >
      <span className="font-semibold">{label}</span>
      <span className="opacity-70 font-mono normal-case tracking-normal">{count}</span>
    </button>
  );
}

export default function ItemsCatalogTab({ onMaterialClick }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [truncated, setTruncated] = useState(false);
  const [selected, setSelected] = useState(null);
  const [total, setTotal] = useState(0);

  // Fetch categories once — they're stable across filter changes.
  useEffect(() => {
    let cancelled = false;
    api.get("/items-catalog/categories")
      .then(({ data }) => {
        if (!cancelled) {
          setCategories(data.categories || []);
          setTotal(data.total || 0);
        }
      })
      .catch((e) => { if (!cancelled) { toast.error(apiError(e)); } });
    return () => { cancelled = true; };
  }, []);

  // Refetch the items list whenever the filter or query changes.
  // Backend caps at 500 results; we surface a "narrow your search"
  // banner via the truncated flag.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (activeCat !== "All") params.category = activeCat;
    if (q.trim()) params.q = q.trim();
    api.get("/items-catalog", { params })
      .then(({ data }) => {
        if (cancelled) return;
        setItems(data.items || []);
        setTruncated(!!data.truncated);
      })
      .catch((e) => { if (!cancelled) { toast.error(apiError(e)); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeCat, q]);

  // Sort alphabetically client-side — the API returns insertion order.
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [items]);

  return (
    <div className="space-y-3" data-testid="items-catalog-root">
      <div className="glass-card p-3 flex items-center gap-2 sticky top-2 z-10 backdrop-blur">
        <Search size={14} className="text-[var(--muted)] shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search every Palworld item (e.g. 'sphere', 'ammo', 'fishing rod')…"
          className="input flex-1"
          data-testid="items-search"
        />
        {q && (
          <button onClick={() => setQ("")} className="btn btn-ghost text-xs" data-testid="items-search-clear">
            Clear
          </button>
        )}
        <span className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-mono hidden sm:flex items-center gap-1">
          <Filter size={11} /> {total.toLocaleString()} total
        </span>
      </div>

      <div className="glass-card p-2 flex flex-wrap gap-1.5" data-testid="items-category-chips">
        <CatChip
          active={activeCat === "All"}
          onClick={() => setActiveCat("All")}
          label="All"
          count={total}
          testid="items-cat-all"
        />
        {categories.map((c) => (
          <CatChip
            key={c.name}
            active={activeCat === c.name}
            onClick={() => setActiveCat(c.name)}
            label={c.name}
            count={c.count}
            testid={`items-cat-${c.name.replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`}
          />
        ))}
      </div>

      {truncated && (
        <div
          className="glass-card p-3 text-xs text-amber-300/90 border-amber-300/30"
          data-testid="items-truncated-warning"
        >
          Showing the first 500 matches — narrow your search or pick a
          category to see the rest.
        </div>
      )}

      {loading && <Loader />}

      {!loading && sorted.length === 0 && (
        <div className="glass-card p-8 text-center text-[var(--muted)] text-sm" data-testid="items-empty">
          No items match these filters.
        </div>
      )}

      {!loading && sorted.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="items-grid">
          {sorted.map((it) => (
            <ItemCard key={it.id} it={it} onClick={() => setSelected(it)} />
          ))}
        </div>
      )}

      {selected && (
        <ItemModal
          it={selected}
          onClose={() => setSelected(null)}
          onUsedInClick={(name) => { setSelected(null); onMaterialClick?.(name); }}
        />
      )}
    </div>
  );
}
